import useInView from '@propertypal/shared/src/hooks/useInView';
import { trackGaEvent } from '@propertypal/shared/src/services/analytics';
import axios, { API_URL } from '@propertypal/shared/src/services/axios';
import { setItem } from '@propertypal/shared/src/services/local.storage';
import { MILLAR_MCCALL_WYLIE, MILLAR_MCCALL_WYLIE_TRANSPARENT } from '@propertypal/shared/src/styles/theme';
import { Property } from '@propertypal/shared/src/types';
import { LegalFeesContact, LegalFeesRequest, legalFeesContactSchema } from '@propertypal/shared/src/types/legal-fees';
import useCachedUser, { PROFILE_STORAGE_KEY } from '@propertypal/web-app/src/hooks/useCachedUser';
import { useFormik } from 'formik';
import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ThemeProvider, useTheme } from 'styled-components';
import config from '../../../../app.config.json';
import Button from '../buttons/Button';
import PhoneNumberInput from '../inputs/PhoneNumberInput';
import TextInput from '../inputs/TextInput';
import { Box } from '../layout';
import { getCaptchaKey } from '../security/Recaptcha';
import { openToast } from '../toast/ToastContext';
import { Bold, Text } from '../typography';
import LegalFeesAbout from './LegalFeesAbout';
import {
  ContactContainer,
  Container,
  LegalContainer,
  LegalTitle,
  Wrapper,
  Logo,
  LogoContainer,
  LegalDetailsContainer,
  LegalDetails,
  Divider,
  FormGrid,
  ContactSentContainer,
  LegalTitleContainer,
  LegalDetailsSubtitle,
  VisitWebsite,
  ConsentContainer,
} from './LegalFeesCalculator.style';
import { Form } from './LegalFeesCalculatorForm.style';
import LegalFeesConsent from './LegalFeesConsent';

interface LegalFeesCalculatorProps {
  hasContact: boolean;
  onCalculateClick: () => void;
  property: Property;
}

const LegalFeesCalculator: React.FC<LegalFeesCalculatorProps> = ({ hasContact, onCalculateClick, property }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [sent, setSent] = useState<boolean>(false);

  const containerRef = useRef<HTMLElement>(null);

  const dispatch = useDispatch();

  const theme = useTheme();

  const formik = useFormik<LegalFeesContact>({
    initialValues: {
      email: '',
      name: '',
      prefix: '+44',
      telephone: '',
      recaptcha: '',
    },
    validationSchema: legalFeesContactSchema,
    onSubmit: async (values) => {
      setLoading(true);

      const recaptchaKey = config.env === 'Production' ? await getCaptchaKey('legalfees') : undefined;

      const data: LegalFeesRequest = {
        name: values?.name?.trim(),
        email: values?.email?.trim(),
        telephone: `${values.prefix}${values.telephone}`,
        recaptcha: recaptchaKey,
        source: 'PDP form',
        propertyId: property?.id,
      };

      try {
        await axios<void>(
          {
            method: 'POST',
            url: `${API_URL}/legalfees/enquiry${config.env === 'Production' ? '-with-recaptcha' : ''}`,
            data,
          },
          dispatch,
        );

        setItem(PROFILE_STORAGE_KEY, {
          name: data.name,
          email: data.email,
          phoneNumber: data.telephone,
        });

        trackGaEvent('legal_fees_enquiry', {
          page: 'PDP',
          location: 'Legal Fees Ad',
          quote: false,
          '500k': !!(property?.price?.price && property?.price?.price > 500000),
          price: property?.price?.priceOnApplication ? 'Price Not Provided' : property?.price?.price,
          address: property?.displayAddress,
          source: data?.source,
        });
        setSent(true);
      } catch {
        openToast({
          message: "Something went wrong. We couldn't send your enquiry. Please try again!",
          type: 'error',
          title: 'Error',
        });
      } finally {
        setLoading(false);
      }
    },
  });

  useCachedUser(property.loggedInUser, (profile) => {
    formik.setValues({
      ...formik.values,
      name: profile.name,
      telephone: profile.phoneNumberNoPrefix,
      prefix: profile.prefix,
      email: profile.email,
    });
  });

  useInView(containerRef, { threshold: 0.5 }, () => {
    trackGaEvent('legal_fees_ad_impression', {
      page: 'PDP',
      location: 'Legal Fees Ad',
      '500k': !!(property?.price?.price && property?.price?.price > 500000),
      price: property?.price?.priceOnApplication ? 'Price Not Provided' : property?.price?.price,
      address: property?.displayAddress,
      source: 'PDP form',
    });
  });

  return (
    <ThemeProvider
      theme={{ ...theme, primary: MILLAR_MCCALL_WYLIE, primaryTranslucent: MILLAR_MCCALL_WYLIE_TRANSPARENT }}
    >
      <Container id="legal-fees-calc" data-testid="legal-fees-calc" ref={containerRef}>
        <Wrapper>
          <LegalContainer>
            <LegalTitleContainer>
              <LegalTitle>Legal Fees Calculator</LegalTitle>
              <Text fontSize={14} fontWeight="300" color={theme.textLight}>
                In partnership with Millar McCall Wylie
              </Text>
            </LegalTitleContainer>

            <LogoContainer>
              <Logo />
            </LogoContainer>

            <LegalDetailsContainer>
              <LegalDetails>
                <LegalDetailsSubtitle
                  color={MILLAR_MCCALL_WYLIE}
                  fontSize={24}
                  mb={hasContact ? 40 : 20}
                  lineHeight={35}
                  maxWidth={460}
                >
                  If you are buying a property in Northern Ireland, you will have to pay legal fees.
                </LegalDetailsSubtitle>

                {hasContact ? (
                  <>
                    <Bold maxWidth={490} fontSize={18}>
                      Due to the property value we will need to provide you with a bespoke quotation. Please contact us
                      directly using the form below.
                    </Bold>
                  </>
                ) : (
                  <>
                    <Text mb={35} maxWidth={430}>
                      Find out how much the legal costs could be by using our fees calculator in partnership with Millar
                      McCall Wylie.
                    </Text>
                    <Button
                      maxWidth="300px"
                      onClick={onCalculateClick}
                      color={MILLAR_MCCALL_WYLIE}
                      testID="calculate-legal-fees"
                    >
                      Calculate Legal Fees
                    </Button>
                  </>
                )}
              </LegalDetails>

              <Divider />

              <LegalFeesAbout />
            </LegalDetailsContainer>
          </LegalContainer>

          {hasContact && (
            <ContactContainer>
              {sent ? (
                <Box data-testid="legal-fees-sent">
                  <ContactSentContainer>
                    <div>
                      <Text fontSize={28} fontWeight="bold">
                        Thank you for contacting Millar McCall Wylie
                      </Text>
                      <Text mt={5} color={MILLAR_MCCALL_WYLIE} fontWeight="bold">
                        A member of our team will be in touch with you shortly.
                      </Text>
                    </div>
                    <VisitWebsite href="https://mmwlegal.com/" target="_blank" rel="noreferrer">
                      <Button maxWidth="320px" color={MILLAR_MCCALL_WYLIE}>
                        Visit our website to meet our team
                      </Button>
                    </VisitWebsite>
                  </ContactSentContainer>
                </Box>
              ) : (
                <Form onSubmit={formik.handleSubmit} data-testid="legal-fees-contact-form" $loading={loading}>
                  <FormGrid>
                    <TextInput
                      name="name"
                      placeholder="Your name"
                      value={formik.values?.name || ''}
                      error={formik.touched.name && formik.errors.name}
                      onValueChange={formik.handleChange('name')}
                      testID="legal-fees-name"
                    />
                    <PhoneNumberInput
                      name="telephone"
                      placeholder="Your telephone number"
                      value={formik.values?.telephone || ''}
                      onValueChange={formik.handleChange('telephone')}
                      prefix={formik.values?.prefix || ''}
                      onPrefixChange={formik.handleChange('prefix')}
                      error={formik.touched.telephone && (formik.errors.telephone || formik.errors.prefix)}
                      testID="legal-fees-tel"
                    />
                    <TextInput
                      name="email"
                      placeholder="Your email address"
                      value={formik.values?.email || ''}
                      error={formik.touched.email && formik.errors.email}
                      onValueChange={formik.handleChange('email')}
                      testID="legal-fees-email"
                    />
                    <Button
                      color={MILLAR_MCCALL_WYLIE}
                      loading={loading}
                      testID="submit-legal-fees-contact"
                      loadingTestID="submitting-legal-fees-contact"
                      type="submit"
                      containerStyle={{ height: '56px' }}
                    >
                      Email Millar McCall Wylie
                    </Button>
                  </FormGrid>
                  <ConsentContainer>
                    <LegalFeesConsent path={property.path} />
                  </ConsentContainer>
                </Form>
              )}
            </ContactContainer>
          )}
        </Wrapper>
      </Container>
    </ThemeProvider>
  );
};

export default LegalFeesCalculator;
